import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ENVIRONMENT } from 'shared';

/** One card we owe a member — the production authorisation the card partner works against. */
export interface CardEntitlement {
  id: number;
  entitlement_id: string;
  user_id: number;
  user_email: string | null;
  user_name: string | null;
  tier: string;
  origin: 'purchase' | 'upgrade' | 'grant';
  status: string;
  partner_card_ref: string | null;
  granted_at: string | null;
  shipped_at: string | null;
  /** When the card partner first fetched this card. null = never picked up. */
  partner_first_lookup_at: string | null;
  partner_last_lookup_at: string | null;
  partner_lookup_count: number;
  requested_by_partner: boolean;
  created_at: string;
}

export interface CardEntitlementDetail extends CardEntitlement {
  card_last4: string | null;
  card_expires_on: string | null;
  carrier: string | null;
  tracking_number: string | null;
  source_type: string;
  source_ref: string;
  accepted_at: string | null;
  produced_at: string | null;
  delivered_at: string | null;
  activated_at: string | null;
  blocked_at: string | null;
  revoked_at: string | null;
  last_event: string | null;
  last_event_reason: string | null;
}

export interface PartnerCardEvent {
  id: number;
  event: string;
  occurred_at: string;
  partner_card_ref: string | null;
  reason: string | null;
  created_at: string;
}

export interface PartnerCardTransaction {
  id: number;
  partner_transaction_id: string;
  transaction_type: string;
  status: string;
  occurred_at: string;
  amount: number;
  currency: string;
  fee: number | null;
  balance_after: number | null;
  merchant_name: string | null;
  decline_reason: string | null;
}

export interface PartnerCardTopup {
  id: number;
  partner_topup_id: string;
  status: string;
  occurred_at: string;
  amount: number;
  currency: string;
  source: string | null;
  balance_after: number | null;
  reason: string | null;
}

/** An address the partner asked about and got nothing for. */
export interface CardLookupRejection {
  id: number;
  email: string;
  reason: 'unknown_email' | 'account_closed' | 'no_entitlement';
  user_id: number | null;
  user_name: string | null;
  attempts: number;
  first_attempt_at: string;
  last_attempt_at: string;
  resolved_at: string | null;
}

export interface CardEntitlementStats {
  total: number;
  by_status: Record<string, number>;
  by_origin: Record<string, number>;
  requested: number;
  not_requested: number;
  shipped: number;
  rejections_open: number;
  rejections_total: number;
  /** Card owners without an entitlement row — the backfill has not run for them. */
  missing_entitlements: number;
  lifetime_grant_enabled: boolean;
}

export interface CardEntitlementListResponse {
  entitlements: CardEntitlement[];
  total_count: number;
  total_pages: number;
  page: number;
  per_page: number;
}

export interface CardEntitlementDetailResponse {
  entitlement: CardEntitlementDetail;
  events: PartnerCardEvent[];
  transactions: PartnerCardTransaction[];
  topups: PartnerCardTopup[];
}

export interface CardLookupRejectionListResponse {
  rejections: CardLookupRejection[];
  total_count: number;
  total_pages: number;
  page: number;
  per_page: number;
}

export interface CardEntitlementFilters {
  page?: number;
  per_page?: number;
  filter_status?: string;
  filter_origin?: string;
  /** 'yes' = already fetched by the partner, 'no' = never fetched. */
  filter_requested?: 'yes' | 'no';
  filter_search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface CardLookupRejectionFilters {
  page?: number;
  per_page?: number;
  filter_reason?: string;
  filter_open?: boolean;
  filter_search?: string;
}

@Injectable({ providedIn: 'root' })
export class CardEntitlementService {
  private readonly apiUrl = this.environment.apiUrl || 'http://localhost:3000/api';
  private readonly base = `${this.apiUrl}/admin/card-entitlements`;

  constructor(private http: HttpClient, @Inject(ENVIRONMENT) private environment: any) {}

  getEntitlements(filters: CardEntitlementFilters = {}): Observable<CardEntitlementListResponse> {
    return this.http.get<CardEntitlementListResponse>(this.base, { params: this.toParams(filters) });
  }

  getEntitlement(id: number): Observable<CardEntitlementDetailResponse> {
    return this.http.get<CardEntitlementDetailResponse>(`${this.base}/${id}`);
  }

  getStats(): Observable<CardEntitlementStats> {
    return this.http.get<CardEntitlementStats>(`${this.base}/stats`);
  }

  getRejections(filters: CardLookupRejectionFilters = {}): Observable<CardLookupRejectionListResponse> {
    return this.http.get<CardLookupRejectionListResponse>(`${this.base}/rejections`, {
      params: this.toParams(filters)
    });
  }

  /** Grant the card to a member the backfill missed — same idempotent path as every other grant. */
  syncUser(userId: number): Observable<{ created: boolean; entitlement: CardEntitlement }> {
    return this.http.post<{ created: boolean; entitlement: CardEntitlement }>(`${this.base}/sync`, {
      user_id: userId
    });
  }

  private toParams(filters: object): HttpParams {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }
}
